import Checkbox from 'src/components/Checkbox';
import React, { ChangeEvent, ReactNode, useState } from 'react';
import ModernInput from 'src/components/SharedComponents/ModernInput/ModernInput';
import ModernDatePicker from 'src/components/SharedComponents/ModernDatePicker/ModernDatePicker';
import { Multiselect } from 'multiselect-react-dropdown';
import Button from 'src/components/Button';
import { getRoleDisplayLabel } from 'src/common/roleRegistry/roleRegistryHelpers';
import './ContactFilterComponents.scss';

interface FilterCheckboxItemProps {
  label: string;
  value: boolean;
  onChange: () => void;
}

const FilterCheckboxItem = ({
  label,
  value,
  onChange,
}: FilterCheckboxItemProps) => {
  return (
    <div className="checkbox-list-item-contacts">
      <Checkbox value={value} onChange={onChange} label={label} />
    </div>
  );
};

interface FilterSectionProps {
  title: string;
  children: ReactNode;
}

const FilterSection = ({ title, children }: FilterSectionProps) => {
  return (
    <div>
      <h3>{title}</h3>
      <div>{children}</div>
    </div>
  );
};

interface RoleFilterProps {
  roles: string[];
  selectedRoles: string[];
  onRoleChange: (role: string) => void;
}

export const RoleFilter = ({
  roles,
  selectedRoles,
  onRoleChange,
}: RoleFilterProps) => {
  return (
    <FilterSection title="Role">
      <div className="checkbox-list-contact">
        {roles.map((role: string) => (
          <FilterCheckboxItem
            key={role}
            label={getRoleDisplayLabel(role)}
            value={selectedRoles.includes(role)}
            onChange={() => onRoleChange(role)}
          />
        ))}
      </div>
    </FilterSection>
  );
};

interface GenericFilterDropdownProps {
  title: string;
  id: string;
  data: any[] | null;
  selectedValue: any;
  onChange: (value: any) => void;
  idField: string;
  nameField: string;
  placeholder: string;
  disabled?: boolean;
  showResetOption?: boolean;
  resetLabel?: string;
}

export const GenericFilterDropdown = ({
  title,
  id,
  data,
  selectedValue,
  onChange,
  idField,
  nameField,
  placeholder,
  disabled = false,
  showResetOption = false,
  resetLabel = 'Reset Selection',
}: GenericFilterDropdownProps) => {
  const dropdownOptions: any =
    data?.map((item: any) => ({
      Id: item[idField],
      name: item[nameField],
    })) || [];

  const handleReset = () => {
    onChange(null);
  };

  const hasSelection: boolean =
    selectedValue && Array.isArray(selectedValue) && selectedValue.length > 0;

  const [isHovered, setIsHovered] = useState(false);

  return (
    <FilterSection title={title}>
      <div className="filter-dropdown">
        <div className="dropdown-container">
          <div className="multiselect-wrapper">
            <Multiselect
              id={id}
              singleSelect
              options={dropdownOptions}
              selectedValues={selectedValue}
              displayValue="name"
              placeholder={placeholder}
              onSelect={onChange}
              onRemove={onChange}
              disable={disabled}
            />
          </div>

          {showResetOption && hasSelection && (
            <div>
              <Button
                color="red"
                size="small"
                label="Clear Filter"
                onClick={handleReset}
                disabled={disabled}
                outlined
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.75rem',
                  minHeight: '4.25rem',
                  borderColor: isHovered ? '#b91c1c' : '#dc2626',
                  backgroundColor: '#ffffff',
                  color: isHovered ? '#b91c1c' : '#dc2626',
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  height: '100%',
                  transition: 'all 0.2s ease-in-out',
                  transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
                  boxShadow: isHovered
                    ? '0 2px 4px rgba(220, 38, 38, 0.1)'
                    : 'none',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </FilterSection>
  );
};

interface StatusFilterProps {
  statuses: string[];
  selectedStatuses: string[];
  onStatusChange: (status: string) => void;
}

export const StatusFilter = ({
  statuses,
  selectedStatuses,
  onStatusChange,
}: StatusFilterProps) => {
  return (
    <FilterSection title="Status">
      <div className="checkbox-list-contact">
        {statuses.map((status: string) => (
          <FilterCheckboxItem
            key={status}
            label={status}
            value={selectedStatuses.includes(status)}
            onChange={() => onStatusChange(status)}
          />
        ))}
      </div>
    </FilterSection>
  );
};

interface TextFilterProps {
  title: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

export const TextFilter = ({
  title,
  value,
  placeholder,
  onChange,
}: TextFilterProps) => {
  return (
    <FilterSection title={title}>
      <ModernInput
        value={value}
        placeholder={placeholder}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        fullWidth
      />
    </FilterSection>
  );
};

interface DateRangeFilterProps {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  onDateChange: (field: 'startDate' | 'endDate', value: Date | null) => void;
  startDateError: string | null;
  endDateError: string | null;
}

export const DateRangeFilter = ({
  dateRange,
  onDateChange,
  startDateError,
  endDateError,
}: DateRangeFilterProps) => {
  return (
    <FilterSection title="Invited Date Range">
      <div className="date-range">
        <div className="date-range-option">
          <ModernDatePicker
            label="Start Date"
            value={dateRange.startDate}
            onChange={(date: any) => onDateChange('startDate', date)}
            placeholder="Select start date"
            fullWidth
            error={startDateError}
          />
        </div>
        <div className="date-range-option">
          <ModernDatePicker
            label="End Date"
            value={dateRange.endDate}
            onChange={(date) => onDateChange('endDate', date)}
            placeholder="Select end date"
            fullWidth
            error={endDateError}
          />
        </div>
      </div>
    </FilterSection>
  );
};
